import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    signal,
    viewChildren,
    ViewEncapsulation
} from '@angular/core';
import { TariffSize, TariffStore, TariffType } from '../../../../../tariff';
import { TariffTableAppearance } from '../tariff.table';
import { IndexToTariffSizePipe } from '../pipes';
import { IncludesTariffSizePipe } from '../pipes/includes-tariff-size.pipe';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { MediaService } from '../../../../../media/media.service';
import { DrawerComponent, TooltipDirective } from '../../../shared';
import { MatIconModule } from '@angular/material/icon';
import { ISbAsset } from '@seven1/model';
import { TailwindService } from '@seven1/angular-storyblok/tailwind';
import { ITariffEntryBlok } from './tariff.entry.model';
import { IImageBlock, ImageComponent } from '@seven1/angular-storyblok-components/image';

const baseImageBlok: Pick<IImageBlock, 'maxWidth' | 'maxWidthTablet' | 'maxWidthDesktop'> = {
    maxWidth: '2rem',
    maxWidthTablet: '2rem',
    maxWidthDesktop: '2rem',
};

@Component({
    selector: 'app-tariff-entry',
    imports: [
        IndexToTariffSizePipe,
        IncludesTariffSizePipe,
        NgTemplateOutlet,
        AsyncPipe,
        DrawerComponent,
        TooltipDirective,
        MatIconModule,
        ImageComponent,
    ],
    templateUrl: './tariff.entry.html',
    styleUrl: './tariff.entry.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        '[class]': '[baseClass, baseClass + "-" + appearance()]',
        '[class.without-size]': '!size()',
    },
})
export class TariffEntry {
    private _tailwind = inject(TailwindService);
    media = inject(MediaService);
    tariffStore = inject(TariffStore);
    static nextId = 0;
    id = `tariff-info-${TariffEntry.nextId++}`;

    block = input.required<ITariffEntryBlok>();

    appearance = input<TariffTableAppearance>('3-columns');
    tariff = input.required<TariffType>();
    size = input<TariffSize>();
    selectedIcon = input.required<ISbAsset>();
    selectedIconActive = input.required<ISbAsset>();
    unselectedIcon = input.required<ISbAsset>();
    topsellerTariff = input<TariffSize>();
    topsellerText = input<string>();

    readonly baseClass = 'app-tariff-entry';
    drawerActive = signal(false);
    openButtonsRef = viewChildren<ElementRef<HTMLButtonElement>>('openButton');

    constructor() {
        let hasMounted = false;
        let prev = this.drawerActive();

        effect(() => {
            const current = this.drawerActive();

            if (!hasMounted) {
                hasMounted = true;
                prev = current;
                return;
            }

            if (prev && !current) {
                    const visibleButton = this.openButtonsRef().find((btnRef) => {
                        const el = btnRef.nativeElement;
                        const style = getComputedStyle(el);
                        return (
                            el.offsetParent !== null &&
                            style.visibility !== 'hidden' &&
                            !el.disabled
                        );
                    });
                    visibleButton?.nativeElement.focus();
            }
            prev = current;
        });
    }
    selectedIconBlok = computed<IImageBlock>(() => {
        return {
            ...baseImageBlok,
            asset: this.selectedIcon(),
        };
    });

    selectedIconActiveBlok = computed<IImageBlock>(() => {
        return {
            ...baseImageBlok,
            asset: this.selectedIconActive(),
        };
    });

    unselectedIconBlok = computed<IImageBlock>(() => {
        return {
            ...baseImageBlok,
            asset: this.unselectedIcon(),
        };
    });

    toggleDrawer(drawer: DrawerComponent, event: MouseEvent | KeyboardEvent | TouchEvent | Event): void {
        event.preventDefault();
        event.stopPropagation();
        drawer.toggle(event);
    }

    public getAllClasses = computed<string[]>(() => {
        return ['block', ...this._tailwind.getDisplayClasses(this.block())];
    });
}
